import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SiteShell from "@/components/feature/SiteShell";
import TitleCard from "@/components/feature/TitleCard";
import { filterTitles, sortTitles } from "@/hooks/useCatalog";
import { trendingTitles } from "@/mocks/titles";

const typeFilters = [
  { key: "all", label: "全部类型" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
] as const;

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const keyword = searchParams.get("q")?.trim() ?? "";
  const typeParam = searchParams.get("type") ?? "all";

  const [input, setInput] = useState(keyword);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useEffect(() => {
    if (!keyword) return;
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 320);
    return () => window.clearTimeout(timer);
  }, [keyword, typeParam]);

  const results = useMemo(() => {
    if (!keyword) return [];
    const type = typeParam === "movie" || typeParam === "tv" ? typeParam : undefined;
    return sortTitles(filterTitles({ keyword, type }), "heat");
  }, [keyword, typeParam]);

  const recommendations = trendingTitles.slice(0, 5);

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = input.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
  };

  const changeType = (nextType: string) => {
    const next = new URLSearchParams(searchParams);
    if (nextType === "all") next.delete("type");
    else next.set("type", nextType);
    navigate(`/search?${next.toString()}`);
  };

  return (
    <SiteShell
      breadcrumb="/search"
      pageTitle={keyword ? `${keyword} - 搜索结果 - 长日光阴` : "搜索影视作品 - 长日光阴"}
      pageDescription={
        keyword
          ? `在长日光阴搜索「${keyword}」的影视作品结果，可按电影或电视剧筛选，快速定位想看的影视内容。`
          : "在长日光阴搜索电影与电视剧，支持按片名、导演与类型关键词检索，快速找到想看的影视作品。"
      }
      pageKeywords="长日光阴,影视搜索,电影搜索,电视剧搜索"
    >
      <div className="pt-16 md:pt-[72px]">
        <section className="w-full border-b border-background-200 bg-primary-950 px-4 py-10 md:px-8 md:py-12">
          <div className="mx-auto max-w-[1440px]">
            <p className="text-[10px] tracking-[0.28em] text-primary-200">SEARCH · 站内检索</p>
            <h1 className="mt-2 font-heading text-2xl font-bold text-background-50 md:text-3xl">
              {keyword ? `「${keyword}」的搜索结果` : "搜索影视作品"}
            </h1>
            <form onSubmit={submit} className="mt-5 flex w-full max-w-2xl items-center gap-2 rounded-full bg-background-50 p-1.5">
              <span className="flex h-8 w-8 items-center justify-center text-foreground-500">
                <i className="ri-search-line text-base" aria-hidden="true"></i>
              </span>
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="输入片名、导演或演员名字"
                aria-label="搜索关键词"
                className="min-w-0 flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="rounded-full bg-secondary-500 px-5 py-2 text-sm font-medium text-primary-950 whitespace-nowrap transition-colors cursor-pointer hover:bg-secondary-400"
              >
                搜索
              </button>
            </form>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-primary-200">
              <span>热门搜索：</span>
              {["长夜之光", "奕夜海图", "春山记", "科幻", "悬疑"].map((hot) => (
                <Link
                  key={hot}
                  to={`/search?q=${encodeURIComponent(hot)}`}
                  className="rounded-full border border-background-200/25 px-2.5 py-1 transition-colors cursor-pointer hover:border-secondary-400 hover:text-secondary-400"
                >
                  {hot}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-8 md:py-10">
          <div className="mx-auto max-w-[1440px]">
            <div className="flex flex-col gap-3 border-b border-background-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-foreground-700">
                {keyword ? (
                  <>
                    关键词 <strong className="font-medium text-primary-700">{keyword}</strong> 共找到{" "}
                    <strong className="font-medium text-accent-700">{results.length}</strong> 部作品
                  </>
                ) : (
                  "请输入关键词后开始搜索"
                )}
              </p>
              <div className="flex items-center gap-1 self-start rounded-full bg-background-100 px-1 py-1 sm:self-auto">
                {typeFilters.map((filter) => (
                  <button
                    key={filter.key}
                    type="button"
                    onClick={() => changeType(filter.key)}
                    className={`rounded-full px-3.5 py-1.5 text-xs whitespace-nowrap transition-colors cursor-pointer ${
                      typeParam === filter.key
                        ? "bg-primary-800 text-background-50 font-medium"
                        : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>

            {!keyword ? (
              <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-500">
                  <i className="ri-search-eye-line text-xl" aria-hidden="true"></i>
                </span>
                <h2 className="mt-4 text-sm font-medium text-foreground-900">搜索你感兴趣的影视作品</h2>
                <p className="mt-2 max-w-sm text-xs leading-relaxed text-foreground-500">
                  支持按片名、英文名、导演、演员与类型关键词检索，例如「悬疑」「沈墨川」。
                </p>
              </div>
            ) : loading ? (
              <div className="mt-10 flex flex-col items-center justify-center gap-3 py-16">
                <span className="h-8 w-8 animate-spin rounded-full border-2 border-background-300 border-t-primary-700"></span>
                <p className="text-xs text-foreground-500">正在检索影视作品…</p>
              </div>
            ) : results.length === 0 ? (
              <div className="mt-8">
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-12 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                    <i className="ri-file-search-line text-xl" aria-hidden="true"></i>
                  </span>
                  <h2 className="mt-4 text-sm font-medium text-foreground-900">未找到相关影视作品</h2>
                  <p className="mt-2 max-w-md text-xs leading-relaxed text-foreground-500">
                    换个关键词试试，或者从下面这些热门作品里挑一部。你也可以清空筛选条件重新搜索。
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate("/search")}
                    className="mt-5 inline-flex items-center gap-1.5 rounded-md border border-background-300 px-4 py-2 text-xs text-foreground-700 whitespace-nowrap transition-colors cursor-pointer hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className="ri-refresh-line" aria-hidden="true"></i>
                    清空关键词
                  </button>
                </div>

                <div className="mt-8">
                  <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">猜你可能想看</h2>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5 lg:grid-cols-5">
                    {recommendations.map((item) => (
                      <TitleCard key={item.id} item={item} />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5 lg:grid-cols-5">
                {results.map((item) => (
                  <TitleCard key={item.id} item={item} />
                ))}
              </div>
            )}

            <div className="mt-10 rounded-lg bg-background-100 px-4 py-3 text-center text-[11px] text-foreground-500">
              搜索结果为前端模拟演示，基于站内演示数据生成，未接入真实数据库。
            </div>
          </div>
        </section>
      </div>
    </SiteShell>
  );
}